import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import MediaManagement from './MediaManagement';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: white;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  option {
    background: #2a5298;
    color: white;
  }
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'success'
})`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ListItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ItemMeta = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SmallButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'danger'
})`
  padding: 0.5rem 1rem;
  background: ${props => props.danger ? '#e74c3c' : '#3498db'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.danger ? '#c0392b' : '#2980b9'};
  }
`;

const TitlesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TitleItem = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TitleInput = styled(Input)`
  flex: 1;
`;

const TitleTypeSelect = styled(Select)`
  width: 150px;
`;

const AddTitleButton = styled(SmallButton)`
  margin-top: 0.5rem;
`;

const RemoveTitleButton = styled(SmallButton)`
  background: #e74c3c;
  
  &:hover {
    background: #c0392b;
  }
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function JarManagement({ showMessage }) {
  const [jars, setJars] = useState([]);
  const [selectedJar, setSelectedJar] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [jarForm, setJarForm] = useState({
    image_url: '',
    display_order: 0,
    status: 'active',
    titles: [{ title: '', title_type: 'subtitle', display_order: 0 }]
  });
  const [loading, setLoading] = useState(false);

  const fetchJars = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/jars`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setJars(data.data);
      }
    } catch (error) {
      showMessage('Error fetching jars', 'error');
    }
  }, [showMessage]);

  useEffect(() => {
    fetchJars();
  }, [fetchJars]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const handleJarSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate that at least one title is provided
      const validTitles = jarForm.titles.filter(t => t.title.trim());
      if (validTitles.length === 0) {
        showMessage('At least one title is required', 'error');
        setLoading(false);
        return;
      }

      

      const jarData = {
        ...jarForm,
        titles: validTitles.map((t, index) => ({
          ...t,
          display_order: index
        }))
      };

      if (selectedJar) {
        await fetch(`${API_BASE_URL}/api/v1/jars/${selectedJar.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(jarData)
        });
        showMessage('Jar updated successfully');
      } else {
        await fetch(`${API_BASE_URL}/api/v1/jars`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(jarData)
        });
        showMessage('Jar created successfully');
      }
      
      resetForm();
      fetchJars();
    } catch (error) {
      showMessage(error.message || 'Error saving jar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaSelect = (mediaFile) => {
    setSelectedMedia(mediaFile);
    setJarForm({...jarForm, image_url: mediaFile.url});
    showMessage('Image selected successfully', 'success');
  };

  const resetForm = () => {
    setJarForm({
      image_url: '',
      display_order: 0,
      status: 'active',
      titles: [{ title: '', title_type: 'subtitle', display_order: 0 }],
      show_title: true,
      show_subtitle: true,
      show_description: true
    });
    setSelectedMedia(null);
    setSelectedJar(null);
  };

  const editJar = (jar) => {
    setSelectedJar(jar);
    
    // Set selected media if jar has an image URL
    if (jar.image_url) {
      setSelectedMedia({
        url: jar.image_url,
        fileName: jar.image_url.split('/').pop() || 'Current Image',
        fileSize: 0 // Unknown size for existing images
      });
    } else {
      setSelectedMedia(null);
    }
    
    setJarForm({
      image_url: jar.image_url || '',
      display_order: jar.display_order,
      status: jar.status,
      titles: jar.titles && jar.titles.length > 0 
        ? jar.titles.map((t, index) => ({
            title: t.title,
            title_type: t.title_type,
            display_order: index
          }))
        : [{ title: '', title_type: 'subtitle', display_order: 0 }]
      ,
      show_title: jar.show_title !== undefined ? jar.show_title : true,
      show_subtitle: jar.show_subtitle !== undefined ? jar.show_subtitle : true,
      show_description: jar.show_description !== undefined ? jar.show_description : true
    });
  };

  const deleteJar = async (jarId) => {
    if (!window.confirm('Are you sure you want to delete this jar and all its titles and subheadings?')) {
      return;
    }
    
    try {
      await fetch(`${API_BASE_URL}/api/v1/jars/${jarId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      showMessage('Jar deleted successfully');
      fetchJars();
    } catch (error) {
      showMessage(error.message || 'Error deleting jar', 'error');
    }
  };

  const addTitle = () => {
    setJarForm(prev => ({
      ...prev,
      titles: [...prev.titles, { title: '', title_type: 'subtitle', display_order: prev.titles.length }]
    }));
  };

  const removeTitle = (index) => {
    if (jarForm.titles.length <= 1) {
      showMessage('At least one title is required', 'error');
      return;
    }
    
    setJarForm(prev => ({
      ...prev,
      titles: prev.titles.filter((_, i) => i !== index).map((t, i) => ({
        ...t,
        display_order: i
      }))
    }));
  };

  const updateTitle = (index, field, value) => {
    setJarForm(prev => ({
      ...prev,
      titles: prev.titles.map((t, i) => 
        i === index ? { ...t, [field]: value } : t
      )
    }));
  };

  const getMainTitle = (titles) => {
    if (!titles || titles.length === 0) return 'Untitled';
    const mainTitle = titles.find(t => t.title_type === 'main');
    return mainTitle ? mainTitle.title : titles[0].title;
  };

  return (
    <Container>
      <Section>
        <SectionTitle>Jar Management</SectionTitle>
        
        <Form onSubmit={handleJarSubmit}>
          <div>
            <div style={{ marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
              Selected Image:
            </div>
            {selectedMedia ? (
              <div style={{ 
                padding: '0.75rem', 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <img 
                  src={selectedMedia.url} 
                  alt={selectedMedia.fileName}
                  style={{ 
                    width: '50px', 
                    height: '50px', 
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#ffffff', fontSize: '0.875rem', fontWeight: '500' }}>
                    {selectedMedia.fileName}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
                    {Math.round(selectedMedia.fileSize / 1024)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMedia(null);
                    setJarForm({...jarForm, image_url: ''});
                  }}
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div style={{ 
                padding: '1rem', 
                background: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '6px',
                border: '1px dashed rgba(255, 255, 255, 0.3)',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                No image selected. Please select an image from the Media Library below.
              </div>
            )}
          </div>
          
          <Input
            type="number"
            placeholder="Display Order"
            value={jarForm.display_order}
            onChange={(e) => setJarForm({...jarForm, display_order: parseInt(e.target.value) || 0})}
            min="0"
          />
          
          <Select
            value={jarForm.status}
            onChange={(e) => setJarForm({...jarForm, status: e.target.value})}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>

          <TitlesContainer>
            <div style={{ color: 'white', marginBottom: '0.5rem', fontWeight: '600' }}>
              Titles ({jarForm.titles.length})
            </div>
            {jarForm.titles.map((title, index) => (
              <TitleItem key={index}>
                <TitleInput
                  type="text"
                  placeholder={title.title_type === 'main' ? 'Main Title' : `${title.title_type} title`}
                  value={title.title}
                  onChange={(e) => updateTitle(index, 'title', e.target.value)}
                />
                <TitleTypeSelect
                  value={title.title_type}
                  onChange={(e) => updateTitle(index, 'title_type', e.target.value)}
                >
                  <option value="main">Main</option>
                  <option value="subtitle">Subtitle</option>
                  <option value="description">Description</option>
                </TitleTypeSelect>
                {jarForm.titles.length > 1 && (
                  <RemoveTitleButton
                    type="button"
                    onClick={() => removeTitle(index)}
                  >
                    Remove
                  </RemoveTitleButton>
                )}
              </TitleItem>
            ))}
            <AddTitleButton
              type="button"
              onClick={addTitle}
            >
              + Add Title
            </AddTitleButton>
          </TitlesContainer>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
            <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={jarForm.show_title !== false}
                onChange={(e) => setJarForm({...jarForm, show_title: e.target.checked})}
              />
              <span>Show Main Title</span>
            </label>
            <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={jarForm.show_subtitle !== false}
                onChange={(e) => setJarForm({...jarForm, show_subtitle: e.target.checked})}
              />
              <span>Show Subtitles</span>
            </label>
            <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={jarForm.show_description !== false}
                onChange={(e) => setJarForm({...jarForm, show_description: e.target.checked})}
              />
              <span>Show Descriptions</span>
            </label>
          </div>

          <Button type="submit" disabled={loading}>
            {selectedJar ? 'Update Jar' : 'Create Jar'}
          </Button>
          
          {selectedJar && (
            <Button 
              type="button" 
              onClick={resetForm}
              style={{ background: '#95a5a6' }}
            >
              Cancel
            </Button>
          )}
        </Form>

        <List>
          {jars.map(jar => (
            <ListItem key={jar.id}>
              <ItemInfo>
                <ItemTitle>{getMainTitle(jar.titles)}</ItemTitle>
                <ItemMeta>
                  Order: {jar.display_order} | Status: {jar.status} | 
                  {jar.titles ? jar.titles.length : 0} titles | {jar.subheading_count || 0} subheadings
                </ItemMeta>
              </ItemInfo>
              <ButtonGroup>
                <SmallButton onClick={() => editJar(jar)}>Edit</SmallButton>
                <SmallButton danger onClick={() => deleteJar(jar.id)}>Delete</SmallButton>
              </ButtonGroup>
            </ListItem>
          ))}
        </List>
      </Section>
      
      <MediaManagement 
        onImageSelect={handleMediaSelect}
        selectedImage={selectedMedia}
      />
    </Container>
  );
}

export default JarManagement;
